import { IdValueType, KeyValueModel, KeyValueType } from '@models/common';
import { KeyValueFormattingModel } from '@models/module/fields/enums';
import { DataListFieldPropertiesModel, DataListType } from '@models/module/fields/main';

export interface FieldDataSource extends KeyValueType<number, string> {
  isUsed?: boolean;
  type?: number;
  parentName?: string;
  parentId?: number;
}

export interface ModuleFieldsFilter {
  type?: number;
  displayType?: number;
  dataSourceId?: number;
  multiple?: number;
  addContractFields?: boolean;
  includeSystemFields?: boolean;
  includeSpecialFields?: boolean;
}

export interface ModuleWorkflow {
  id: number;
  workflowGroupId: number;
  workflowGroupName: string;
  isDefault?: boolean;
}

export interface ModuleWorkflowsResponse {
  workflows: ModuleWorkflow[];
  canAddWorkflow?: boolean;
  /** The groups a workflow can still be added to, moved to, or duplicated into. */
  availableWorkflowGroups?: KeyValueModel<number, string>[];
}

export interface ModuleSignature {
  id: number;
  /** Composed by the backend for the printout; the list shows it as the row title. */
  name?: string;
  stateId: number;
  roleId: number;
  /** `-1` stands for the role's primary user. */
  userId: number;
  isBinding: boolean;
}

export interface TransitionGroupEntry {
  transitionId?: number;
  transitionName?: string;
  conditionalId?: number;
  conditionalName?: string;
  order?: number;
}

export interface TransitionGroup {
  id: number;
  name: string;
  transitions: TransitionGroupEntry[];
  conditionalTransitions: TransitionGroupEntry[];
  transitionIds: number[];
  conditionalIds: number[];
}

export interface TransitionGroupOrderPayload {
  transitions: TransitionGroupEntry[];
  conditionalTransitions: TransitionGroupEntry[];
}

export interface EmailLinkMapping {
  correspondenceFieldId: number;
  fieldId: number;
}

export interface EmailLinkModel {
  id: number;
  moduleId: number;
  stateId: number;
  screenId: number;
  hasApprroval: boolean;
  mapping: EmailLinkMapping[];
}

export interface ModuleDataList {
  id: number;
  name: string;
  relationId?: number;
  screenId?: number;
  screenName?: string;
  moduleDataListTypeId?: DataListType;
  isUsed?: boolean;
}

export interface ModuleDataListSync {
  id: number;
  name: string;
  screenId?: number;
  relationId?: number;
  fields: DataListFieldPropertiesModel[];
}

export interface ModuleDataListWithFields extends ModuleDataList {
  fields: DataListFieldPropertiesModel[];
}

export interface DataListComplexFormulaModel {
  id: number;
  label: string;
  expression: string;
  x: number;
  y: number;
  cols: number;
  rows: number;
  typeId: number;
  displayTypeId: number;
  formattings: KeyValueFormattingModel[];
}

export interface DataListAggregateFormulas {
  key: number;
  fields: number[];
}

export interface ReportFieldModel {
  key: number;
  value: string;
  name: string;
  isUsed: boolean;
  isDisabled: boolean;
}

export enum ModuleRelationType {
  Relation = 1,
  Payment = 2,
  Dependency = 3,
  Nested = 4,
}

export interface ModuleRelation {
  id: number;
  name: string;
  typeId?: number;
  typeName?: string;
  parentId: number;
  childId: number;
  parentTypeId?: number;
  childTypeId?: number;
  parentStateTypeIds?: string[];
  childStateTypeIds?: string[];
  allowChanges?: boolean;
  uniqueness?: boolean;
  /** False when the module being configured is the child of the relation. */
  isParent?: boolean;
  parentName?: string;
  childName?: string;
  parentTypeName?: string;
  childTypeName?: string;
  parentFieldName?: string;
  childFieldName?: string;
  /** The number field of the child a payment, or a partial payment, is made on. */
  childFieldId?: number;
  inUse?: boolean;
  relationType: ModuleRelationType;
  /** Null without a payment relation to the progress payment module, false without a partial payment field. */
  partialPayment?: boolean;
  partialPaymentRelationId?: number;
  canPayOver100?: boolean;
}

export interface ModuleRelationStateMapping {
  parentStateId: number;
  childStateId: number;
}

export interface ModulePresetModel {
  id: number;
  name: string;
  description?: string;
  screenId: number;
  /** The contract type the preset is offered on. */
  type?: number;
  typeName?: string;
  moduleDocumentNumber?: string;
  documentNumber?: number;
  documentNoMinor?: number;
  entityId?: number;
  contractIds?: number[];
  values?: IdValueType<unknown, number>[];
  /** The values by field id, plus `<fieldId>_dataSource` for the options a list field was saved with. */
  valuesLookup?: Record<string, unknown>;
}

export interface PresetNumberOption {
  key: string;
  value: string;
  number?: number;
  minor?: number;
}

export const entryId = (entry: TransitionGroupEntry) => entry.transitionId ?? entry.conditionalId;
export const entryName = (entry: TransitionGroupEntry) => entry.transitionName ?? entry.conditionalName;

/** Splits a flat selection back into the four arrays the backend expects. */
export const splitTransitions = (entries: TransitionGroupEntry[]) => ({
  transitions: entries.filter(entry => entry.transitionId !== undefined),
  conditionalTransitions: entries.filter(entry => entry.conditionalId !== undefined),
});
